import { BotIcon, UserRoundCheckIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import type { SubmissionDecisionModeApi } from '@/modules/jobs/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Label } from '@/modules/ui/components/label/Label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/modules/ui/components/radio-group/RadioGroup';

interface SubmissionDecisionModeSettingsProps {
  mode?: SubmissionDecisionModeApi | null;
  isPending: boolean;
  onChange: (mode: SubmissionDecisionModeApi) => void;
}

const MODE_OPTIONS: Array<{
  value: SubmissionDecisionModeApi;
  label: string;
  description: string;
  icon: typeof UserRoundCheckIcon;
}> = [
  {
    value: 'Human',
    label: 'Human decisions',
    description: 'Newly assessed candidates wait for recruiter action.',
    icon: UserRoundCheckIcon,
  },
  {
    value: 'AI',
    label: 'AI decisions',
    description:
      'Agent rejections are rejected; approvals wait for future submission planning.',
    icon: BotIcon,
  },
];

export function SubmissionDecisionModeSettings({
  mode,
  isPending,
  onChange,
}: SubmissionDecisionModeSettingsProps) {
  const currentMode = mode ?? 'Human';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle>Submission decision mode</CardTitle>
          <Badge variant="outline" size="sm">
            {currentMode}
          </Badge>
        </div>
        <CardDescription>
          Choose who determines the outcome after candidate assessment. Human is
          the default for new and legacy jobs.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
          <RadioGroup
            value={currentMode}
            disabled={isPending}
            aria-label="Submission decision mode"
            className="grid gap-3 lg:grid-cols-2"
            onValueChange={(value) =>
              onChange(value as SubmissionDecisionModeApi)
            }
          >
            {MODE_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = currentMode === option.value;

              return (
                <Label
                  key={option.value}
                  htmlFor={`submission-decision-mode-${option.value}`}
                  className={cn(
                    'hover:border-primary/50 flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-colors',
                    isSelected && 'border-primary bg-primary/5',
                    isPending && 'cursor-wait opacity-70',
                  )}
                >
                  <RadioGroupItem
                    id={`submission-decision-mode-${option.value}`}
                    value={option.value}
                    className="mt-1"
                  />
                  <span className="flex min-w-0 gap-3">
                    <span
                      className={cn(
                        'bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg',
                        isSelected && 'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon className="size-5" aria-hidden />
                    </span>
                    <span className="space-y-1">
                      <span className="block font-medium text-black">
                        {option.label}
                      </span>
                      <span className="text-muted-foreground block text-sm leading-5 font-normal">
                        {option.description}
                      </span>
                    </span>
                  </span>
                </Label>
              );
            })}
          </RadioGroup>
        </PermissionsGate>
      </CardContent>
    </Card>
  );
}
